import { useEffect, useRef, useState } from 'react';
import { ActionIcon, Alert, Box, Button, Code, Group, Loader, Stack, Switch, Text, Title } from '@mantine/core';
import { useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useConsoleNav, useConsolePrefix } from '../shell/nav';
import {
  AutoGrid,
  ChipRail,
  ColLabel,
  NotConnected,
  Pill,
  ProviderPill,
  SectionCard,
  uiClasses,
} from '../ui';
import { dateTime, num } from '../ui/format';
import { tokens as T, quotaColor, statusTone } from '../theme/tokens';
import { usePoolStore } from '../stores/dataStores';
import { poolKeyExpiry, poolKeyState, type PoolKey, type PoolKeyExpiry } from '../data/types';
import { useUIStore } from '../stores/uiStore';
import { getSources } from '../stores/sources';
import { READ_ONLY } from './PoolPage';
import { passed, testPoolKey, type ProbeResult } from '../api/probe';
import { getPoolKey, isOverflowKey, poolKeyName, setPoolKeyEnabled } from '../api/pool';
import { ApiError } from '../api/client';
import { latency } from '../ui/format';

export function PoolKeyPage() {
  const { keyId = '' } = useParams();
  const navigate = useConsoleNav();
  const pool = usePoolStore((s) => s.data);
  const load = usePoolStore((s) => s.load);
  const upsert = usePoolStore((s) => s.upsert);
  const drop = usePoolStore((s) => s.drop);
  const openModal = useUIStore((s) => s.openModal);

  // The probe is deliberately NOT behind canMutate. That flag gates the six
  // credential-editing modals, none of which have a route yet; this one does,
  // and lighting it up alongside them would imply the rest work too.
  // A probe reaches the REAL router and spends a shared subscription's quota, so
  // it is locked out of the fixture harness by the route as well as by the
  // source kind — the kind can flip to live while sample rows are still on
  // screen, and the route cannot.
  const harness = useConsolePrefix() === '/dev';
  const live = !harness && getSources().live.kind === 'live';
  const [toggling, setToggling] = useState(false);

  // The enable switch (DR-99): the response is the updated row.
  async function toggle(id: string, enabled: boolean) {
    if (!live || toggling) return;
    setToggling(true);
    try {
      upsert(id, await setPoolKeyEnabled(id, enabled));
    } catch (err) {
      notifications.show({ color: 'red', title: enabled ? 'Not enabled' : 'Not disabled', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setToggling(false);
    }
  }
  const currentIdRef = useRef(decodeURIComponent(keyId));
  currentIdRef.current = decodeURIComponent(keyId);
  const [probing, setProbing] = useState(false);
  const [probe, setProbe] = useState<ProbeResult | null>(null);

  const currentId = decodeURIComponent(keyId);

  /** Re-read this one credential. The page shows one key; refetching the whole
   *  pool to update it is work nobody asked for and a table-wide re-render. */
  async function refreshRow(id: string) {
    try {
      upsert(id, await getPoolKey(id));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) drop(id);
    }
  }

  // React Router reuses this component between key URLs, so without this a
  // result for key A stays on screen under key B's heading — reading as a
  // verdict on a credential it never touched.
  useEffect(() => {
    setProbe(null);
    setProbing(false);
  }, [currentId]);

  async function runProbe(id: string) {
    if (!live) return;
    setProbing(true);
    setProbe(null);
    const settle = (r: ProbeResult) => {
      // A probe is a provider round trip. If the operator navigated to another
      // key while it ran, its answer belongs to a page that is no longer here.
      setProbe((prev) => (id === currentIdRef.current ? r : prev));
    };
    try {
      settle(await testPoolKey(id));
    } catch (err) {
      settle({
        poolKeyId: id,
        passed: false,
        outcome: 'did_not_complete',
        detail: err instanceof Error ? err.message : String(err),
      });
    } finally {
      if (id === currentIdRef.current) setProbing(false);
      // A probe can mint or refresh a credential, so the stored record it just
      // changed is re-read — but only THIS row. Refetching the whole pool to
      // show one key's new quota hands React a new object for every other row
      // as well, which is the flashing the update discipline exists to prevent.
      try {
        upsert(id, await getPoolKey(id));
      } catch (err) {
        // The probe's verdict is already on screen and stands on its own. A
        // failed re-read means the numbers below it are stale, not that the
        // probe failed, so it must not overwrite what the probe proved.
        if (err instanceof ApiError && err.status === 404) drop(id);
      }
    }
  }

  useEffect(() => {
    if (pool.phase === 'unconfigured') load(undefined as void);
  }, [pool.phase, load]);

  const key = pool.byId[decodeURIComponent(keyId)];
  if (!key) {
    return (
      <Stack gap={16}>
        <Group gap={14}>
          <ActionIcon variant="default" radius={999} size={36} onClick={() => navigate('/pool')}>
            ←
          </ActionIcon>
          <Title order={2}>Pool key</Title>
        </Group>
        <NotConnected what="this credential" />
      </Stack>
    );
  }

  const state = poolKeyState(key);
  const windows = key.quotaWindows ?? [];

  return (
    <Stack gap={16}>
      <Group justify="space-between" align="center" gap={20} wrap="wrap">
        <Group gap={14} wrap="nowrap" style={{ minWidth: 0 }}>
          <ActionIcon variant="default" radius={999} size={36} onClick={() => navigate('/pool')}>
            ←
          </ActionIcon>
          <Title order={2} c={key.label.trim() ? undefined : T.ink4}>
            {poolKeyName(key)}
          </Title>
          <ProviderPill provider={key.provider} size="xl" />
          <Pill tone={statusTone(state)} size="xl">
            {state}
          </Pill>
          {isOverflowKey(key) ? (
            <Pill tone="neutral" size="xl">
              OVERFLOW
            </Pill>
          ) : null}
        </Group>
        <Group gap={12}>
          <Button variant="default" onClick={() => void refreshRow(key.id)}>
            ⟳ Refresh
          </Button>
          <Button
            loading={probing}
            disabled={!live}
            onClick={() => void runProbe(key.id)}
            title={
              live
                ? 'Serves one trivial completion with this credential. It spends quota.'
                : 'These rows are fixtures — a test would reach the real pool.'
            }
          >
            {probing ? 'Probing…' : 'Test key'}
          </Button>
          <Button variant="default" onClick={() => openModal({ kind: 'editKey', keyId: key.id })}>
            Edit
          </Button>
          <Group
            gap={10}
            wrap="nowrap"
            style={{
              height: 38,
              padding: '0 16px',
              borderRadius: 999,
              background: T.card,
              border: `1px solid ${T.border}`,
            }}
          >
            <Switch
              checked={key.enabled}
              disabled={!live || toggling}
              title={live ? (key.enabled ? 'Disable this key' : 'Enable this key') : READ_ONLY}
              onChange={(e) => void toggle(key.id, e.currentTarget.checked)}
              classNames={{ input: uiClasses.switchInput, track: uiClasses.switchTrack }}
              styles={{
                root: { '--switch-height': '19px', '--switch-width': '34px', '--switch-thumb-size': '14px' },
              }}
              aria-label="Enabled"
            />
            <Text fz={13} fw={700} c={key.enabled ? T.ink : T.ink4}>
              {key.enabled ? 'Enabled' : 'Disabled'}
            </Text>
          </Group>
          <ActionIcon
            variant="subtle"
            c={T.danger}
            onClick={() => openModal({ kind: 'deleteKey', keyId: key.id })}
            title="Delete key"
          >
            🗑
          </ActionIcon>
        </Group>
      </Group>
      <Text ff="monospace" fz={13} c={T.ink5} ml={50} mt={-8}>
        {key.id}
      </Text>

      {probing || (probe && probe.poolKeyId === key.id) ? (
        <ProbeReport running={probing} result={probe} expiry={poolKeyExpiry(key)} />
      ) : null}

      <AutoGrid min={280} gap={16}>
        <SectionCard
          p="18px 20px"
          title={undefined}
        >
          <Group justify="space-between" align="center">
            <ColLabel>Quota windows</ColLabel>
            {key.planType ? (
              <Box
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  padding: '5px 10px',
                  borderRadius: 999,
                  background: T.rail,
                  color: T.ink4,
                }}
              >
                {key.planType}
              </Box>
            ) : null}
          </Group>
          {/* A bare heading over empty space reads as a broken panel. Not every
              provider reports windowed quota, and a key that has not served
              since the last refresh has none observed yet — both are facts, and
              both are different from "this key is at 0%". */}
          {windows.length === 0 ? (
            <Text fz={13} c={T.ink4} mt={12} lh={1.55}>
              No quota window reported. This provider sends no windowed rate-limit headers, or this
              key has not served a completion since the last refresh — press <b>Test key</b> to make
              it report.
            </Text>
          ) : null}
          {windows.map((w) => (
            <Box key={w.kind} mt={14}>
              <Group justify="space-between" align="baseline">
                <Text fz={22} fw={800}>
                  {w.label}
                </Text>
                <Text fz={13} fw={700} c={quotaColor(w.usedPercent)}>
                  {w.usedPercent}%
                </Text>
              </Group>
              <Box mt={8} style={{ height: 7, borderRadius: 999, background: T.page, overflow: 'hidden' }}>
                <Box
                  style={{
                    height: 7,
                    borderRadius: 999,
                    width: `${Math.max(w.usedPercent, 2)}%`,
                    background: quotaColor(w.usedPercent),
                  }}
                />
              </Box>
              <Text fz={12} c={T.ink5} mt={7}>
                {w.resetAt ? `resets ${dateTime(w.resetAt)}` : 'reset time unknown'}
              </Text>
            </Box>
          ))}
        </SectionCard>

        <SectionCard p="18px 20px">
          <ColLabel>Lifetime activity</ColLabel>
          <Box
            mt={16}
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px 12px' }}
          >
            <Stat label="Calls served" value={num(key.callCount)} />
            <Stat label="Failures" value={num(key.failureCount)} color={key.failureCount ? T.danger : undefined} />
            <Stat label="Tokens in" value={num(key.inputTokens)} />
            <Stat label="Tokens out" value={num(key.outputTokens)} />
          </Box>
        </SectionCard>

        <SectionCard p="18px 20px">
          <ColLabel>Timeline</ColLabel>
          <Box
            mt={16}
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 12px' }}
          >
            <Small label="Last used" value={dateTime(key.lastUsedAt)} />
            <Small label="Last failure" value={dateTime(key.lastFailureAt)} color={key.lastFailureAt ? T.danger : undefined} />
            <CredentialDates poolKey={key} />
          </Box>
        </SectionCard>
      </AutoGrid>

      <SectionCard p="18px 20px">
        <ColLabel>Scope &amp; selectability</ColLabel>
        <Box mt={14}>
          <ChipRail
            value={key.scope || 'shared'}
            onChange={() => {}}
            data={[
              { value: 'shared', label: 'Shared — all estates' },
              { value: 'sw', label: 'Reserved · sw' },
              { value: 'am', label: 'Reserved · am' },
              { value: 'hal', label: 'Reserved · hal' },
            ]}
          />
        </Box>
        <Text fz={12} c={T.ink4} mt={12} lh={1.6} maw={820}>
          Scope governs who may <em>select</em> this key, never who may refresh it — the refresh
          lease stays keyed on the document id. Changing scope invalidates the selection cache
          immediately.
        </Text>
      </SectionCard>

      {key.lastError ? (
        <Box
          style={{
            background: T.dangerSoft,
            border: `1px solid ${T.dangerBorder}`,
            borderRadius: 14,
            padding: '16px 20px',
          }}
        >
          <ColLabel>
            <Text component="span" c={T.danger} fz={10} fw={700} style={{ letterSpacing: '0.09em' }}>
              LAST ERROR
            </Text>
          </ColLabel>
          {state === 'EXHAUSTED' ? (
            <Text fz={13} c={T.coralDeep} mt={8} lh={1.6} maw={820}>
              The provider reported the account's funding is spent, so the pool turned this key off.
              An API key has no quota window that resets, and nothing re-enables it automatically:
              fund the account, run <em>Test key</em> until it passes (a pass clears this error), then
              enable the key by hand.
            </Text>
          ) : null}
          <Text ff="monospace" fz={13} c={T.coralDeep} mt={8} lh={1.6} style={{ wordBreak: 'break-word' }}>
            {key.lastError}
          </Text>
        </Box>
      ) : null}

      <SectionCard p="18px 20px">
        <AutoGrid min={220} gap={16}>
          <Small label="Created" value={dateTime(key.createdAt)} />
          <Small label="Updated" value={dateTime(key.updatedAt)} />
          <Small label="Updated by" value={key.updatedBy} />
        </AutoGrid>
      </SectionCard>
    </Stack>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Box>
      <Text fz={13} c={T.ink3}>
        {label}
      </Text>
      <Text fz={24} fw={800} mt={2} c={color}>
        {value}
      </Text>
    </Box>
  );
}

/** The two credential dates, labelled by what they name. For a rotating key
 *  it is the access token the sweep renews; for Meta the model key re-minted
 *  on demand, which running out is routine and not a lapse (DR-57). An API key
 *  has neither, and "never" twice under a label about tokens read as a fault.
 *
 *  The first date is `lastRefresh`, which a mint, a refresh AND an operator
 *  re-seed all move — so for Meta it is "the credential was renewed", not
 *  "the model key was minted": a re-seed of the identity alone moves it too. */
function CredentialDates({ poolKey }: { poolKey: PoolKey }) {
  switch (poolKeyExpiry(poolKey)) {
    case 'access_token':
      return (
        <>
          <Small label="Token refresh" value={dateTime(poolKey.lastRefresh)} />
          <Small label="Access token expires" value={dateTime(poolKey.expiresAt)} />
        </>
      );
    case 'model_key':
      return (
        <>
          <Small label="Credential renewed" value={dateTime(poolKey.lastRefresh)} />
          <Small label="Model key expires" value={dateTime(poolKey.expiresAt)} />
        </>
      );
    default:
      return null;
  }
}

function Small({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Box>
      <Text fz={13} c={T.ink3}>
        {label}
      </Text>
      <Text fz={14} fw={600} mt={3} c={color}>
        {value}
      </Text>
    </Box>
  );
}

/** What the probe proved.
 *
 *  A pass says "Passed" and stops talking. The API still grades WHICH half of a
 *  subscription credential served the completion (DR-33) and `haus pool test`
 *  still prints that grade — but on this screen the evidence is already in the
 *  field row below, and leading a green result with a caveat about it buried the
 *  answer the operator pressed the button for. The prose is kept for the cases
 *  where prose IS the answer: a refusal, a timeout, an inconclusive run.
 */
function ProbeReport({
  running,
  result,
  expiry,
}: {
  running: boolean;
  result: ProbeResult | null;
  /** Which short-lived credential the probe's expiry names — see CredentialDates. */
  expiry: PoolKeyExpiry;
}) {
  if (running) {
    return (
      <SectionCard p="16px 20px">
        <Group gap={12}>
          <Loader size="sm" />
          <Box>
            <Text fz={14} fw={700}>
              Probing the credential
            </Text>
            <Text fz={12} c={T.ink4} mt={2}>
              Serving one trivial completion through this key. A cold subscription with a reasoning
              verify-model can take a while.
            </Text>
          </Box>
        </Group>
      </SectionCard>
    );
  }
  if (!result) return null;

  const ok = passed(result);
  const tone = ok ? T.okFg : T.danger;
  const heading = ok
    ? 'Passed'
    : result.outcome === 'inconclusive'
      ? 'Inconclusive — this proved nothing'
      : result.outcome === 'did_not_complete'
        ? // Deliberately not "refused". Nothing here says the credential is
          // bad, and retiring a working subscription over a timeout is the
          // expensive mistake.
          'Did not complete — this says nothing about the credential'
        : 'Failed — the provider refused this credential';

  return (
    <SectionCard p="16px 20px">
      <Group justify="space-between" align="flex-start" gap={16} wrap="nowrap">
        <Box style={{ minWidth: 0 }}>
          <Text fz={14} fw={800} c={tone}>
            {heading}
          </Text>
          {/* Only when the prose IS the answer. A pass has the facts below it. */}
          {!ok && result.detail ? (
            <Text fz={13} c={T.ink3} mt={5} lh={1.6}>
              {result.detail}
            </Text>
          ) : null}
        </Box>
        {result.latencyMs !== undefined ? (
          <Text fz={12} c={T.ink4} style={{ whiteSpace: 'nowrap' }}>
            {latency(result.latencyMs)}
          </Text>
        ) : null}
      </Group>

      {ok ? (
        <Group gap={22} mt={14} wrap="wrap">
          <Field label="SERVED BY" value={result.model || '—'} mono />
          <Field label="ASKED FOR" value={result.requestedModel || '—'} mono />
          <Field label="TOKENS" value={`${result.inputTokens ?? 0} in · ${result.outputTokens ?? 0} out`} />
          {result.expiresAt ? (
            <Field
              label={expiry === 'model_key' ? 'STORED MODEL KEY EXPIRES' : 'STORED ACCESS TOKEN EXPIRES'}
              value={dateTime(result.expiresAt)}
            />
          ) : null}
        </Group>
      ) : null}

      {ok && result.reply ? (
        <Box mt={12}>
          <ColLabel mb={4}>REPLY</ColLabel>
          {/* The content is trivial by construction — it is evidence that
              something was served, not an answer anyone wants. */}
          <Code block>{result.reply}</Code>
        </Box>
      ) : null}

      {result.uncappedOutput ? (
        <Alert color="yellow" variant="light" mt={14} p="10px 14px">
          <Text fz={12.5} lh={1.55}>
            This provider does not accept an output cap, so the probe spent whatever the model
            chose to spend — on a subscription every estate shares.
          </Text>
        </Alert>
      ) : null}
    </SectionCard>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Box>
      <ColLabel mb={3}>{label}</ColLabel>
      <Text fz={13} fw={600} ff={mono ? 'monospace' : undefined}>
        {value}
      </Text>
    </Box>
  );
}
